import * as THREE from 'three';
import { ThreeSixtyViewer } from '../ThreeSixtyViewer';
import { Utilities } from '../Utilities';
import { ObservationData } from '../Types';
import { IViewerElement } from './ViewerElement';

export class ViewerObservation implements IViewerElement {
  private static readonly WIDTH: number = 30;
  private static readonly HEIGHT: number = 30;
  private static readonly BORDER_THICKNESS = 3;
  public color: number;
  public status: string;
  public observationData: ObservationData[];
  public readonly id: string;
  public position: THREE.Vector2 = new THREE.Vector2();
  private _observation3dObject: THREE.Object3D;
  public get element3dObject(): THREE.Object3D {
    return this._observation3dObject;
  }

  /**
   * Constructs a new Observation instance with specified properties.
   * @param position - Position on the 3D image.
   * @param id - Unique identifier for the observation.
   * @param observationData - Associated observation data.
   * @param color - Color used for the observation borders to represent the observation status.
   */
  constructor(
    position: THREE.Vector2,
    id: string,
    observationData: ObservationData[],
    color: number,
    status: string,
  ) {
    this.id = id;
    this.color = color;
    this.status = status;
    this.observationData = observationData;
    this.position = position;
    this.position.z = 0;
    this._observation3dObject = this.createBillboardRectangleOnSphere();
    this._observation3dObject.name = this.id;
  }

  /**
   * Creates a 3D rectangular billboard on the sphere.
   * @returns The created 3D object representing the observation billboard.
   */
  private createBillboardRectangleOnSphere(): THREE.Object3D {
    const positionOnSphere: THREE.Vector3 = Utilities.KrpanoPositionToViewerPosition(
      this.position.x,
      this.position.y,
    );

    const innerGeometry = new THREE.PlaneGeometry(
      ViewerObservation.WIDTH,
      ViewerObservation.HEIGHT,
    );
    const innerMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x000000),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.2,
      depthWrite: false, // Don't write to depth buffer for transparent objects
      depthTest: true,
    });
    const innerRectangle = new THREE.Mesh(innerGeometry, innerMaterial);
    
    // Set high render order to ensure it renders after tiles
    innerRectangle.renderOrder = 999;

    const borderGroup = this.createBorderPlanes();
    
    // Also set render order for borders
    borderGroup.children.forEach(child => {
      if (child instanceof THREE.Mesh) {
        child.renderOrder = 1000; // Borders render after inner rectangle
      }
    });

    const group = new THREE.Object3D();
    group.add(innerRectangle);
    group.add(borderGroup);
    group.position.copy(positionOnSphere);
    group.updateMatrixWorld(true);
    return group;
  }

  private createBorderPlanes(): THREE.Object3D {
    const borderGroup = new THREE.Object3D();
    const halfWidth = ViewerObservation.WIDTH / 2;
    const halfHeight = ViewerObservation.HEIGHT / 2;
    const borderThickness = ViewerObservation.BORDER_THICKNESS; // Thickness of the borders
    const borderMaterial = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      color: this.color, // Border color
    });

    // Top border
    const topBorder = new THREE.Mesh(
      new THREE.PlaneGeometry(ViewerObservation.WIDTH, borderThickness), // Full width, border height
      borderMaterial,
    );
    topBorder.position.set(0, halfHeight + borderThickness / 2, 0); // Above the rectangle

    // Bottom border
    const bottomBorder = new THREE.Mesh(
      new THREE.PlaneGeometry(ViewerObservation.WIDTH, borderThickness), // Full width, border height
      borderMaterial,
    );
    bottomBorder.position.set(0, -halfHeight - borderThickness / 2, 0); // Below the rectangle

    // Left border
    const leftBorder = new THREE.Mesh(
      new THREE.PlaneGeometry(borderThickness, ViewerObservation.HEIGHT + borderThickness * 2), // Border width, full height
      borderMaterial,
    );
    leftBorder.position.set(-halfWidth - borderThickness / 2, 0, 0); // Left side of the rectangle

    // Right border
    const rightBorder = new THREE.Mesh(
      new THREE.PlaneGeometry(borderThickness, ViewerObservation.HEIGHT + borderThickness * 2), // Border width, full height
      borderMaterial,
    );
    rightBorder.position.set(halfWidth + borderThickness / 2, 0, 0); // Right side of the rectangle

    // Add borders to the group
    borderGroup.add(topBorder);
    borderGroup.add(bottomBorder);
    borderGroup.add(leftBorder);
    borderGroup.add(rightBorder);

    return borderGroup;
  }
}
